import { useEffect, useState } from "react";
import {
  Calendar,
  ChevronDown,
  MoreVertical,
  Pencil,
  Trash2
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useMachineOperations } from "@/hooks/use-machines";
import {
  useInvalidateQueries,
  usePutData
} from "@/hooks/services/use-query-hooks";
import { UpdateMachineStatus } from "@/hooks/services/pps/handlers/machines-api";
import { MachineGroupedItem } from "@/hooks/services/pps/types/machines.type";

import { cn } from "@/lib/utils";
import { toastManager } from "@/lib/toast-manager";
import { extractApiMessages } from "@/lib/parse-api-errors";

export function MachineCard({
  machine,
  onEdit
}: {
  machine: MachineGroupedItem;
  onEdit: (machineId: string) => void;
}) {
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const statusConfig = {
    Available: {
      id: 1,
      color: "aero:bg-green-500",
      textColor: "aero:!text-green-700",
      bgColor: "aero:bg-green-50",
      borderColor: "aero:border-green-600",
      label: "Available"
    },
    Maintenance: {
      id: 2,
      color: "aero:bg-yellow-500",
      textColor: "aero:!text-yellow-700",
      bgColor: "aero:bg-yellow-50",
      borderColor: "aero:border-yellow-600",
      label: "Maintenance"
    },
    Unavailable: {
      id: 3,
      color: "aero:bg-red-500",
      textColor: "aero:!text-red-700",
      bgColor: "aero:bg-red-50",
      label: "Unavailable"
    }
  };

  const config = statusConfig[machine.status.name as keyof typeof statusConfig];
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const updateMachineStatus = usePutData(UpdateMachineStatus());
  const { deleteMachine, isDeleting } = useMachineOperations();
  const invalidate = useInvalidateQueries();

  const handleStatusChange = (newStatus: number) => {
    updateMachineStatus.mutate(
      { machineId: machine?.id, status: newStatus },
      {
        onError: (error: any) => {
          toastManager.showError(
            error?.response?.data?.message || "Failed to update machine status."
          );
        },
        onSettled: () => {
          invalidate([
            ["MachineListGroupedByShop"],
            ["TechnicianGroupedByShop"],
            ["JobSchedule"],
            ["TechnicianSchedule"],
            ["MachineSchedule"],
            ["MachineDetails", String(machine.id)]
          ]);
        }
      }
    );
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    deleteMachine(machine.id.toString())
      .then(() => {
        toastManager.showSuccess("Machine deleted successfully");
        invalidate([
          ["MachineListGroupedByShop"],
          ["MachineList"],
          ["MachineDetails"],
          ["TechnicianList"],
          ["TechnicianGroupedByShop"]
        ]);
      })
      .catch((error: unknown) => {
        toastManager.showError(
          extractApiMessages(
            error,
            "Failed to delete machine. Please try again."
          )
        );
      });
  };

  useEffect(() => {
    return () => {
      setTimeout(() => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.body.removeAttribute("data-scroll-locked");
      }, 0);
    };
  }, []);

  const visibleUsersCount = 3;

  return (
    <>
      <Card className="aero:transition-all aero:duration-200 aero:hover:shadow-md aero:border-l-4 aero:border-l-blue-500 aero:overflow-hidden aero:p-0">
        <CardContent className="aero:p-4">
          {/* Header Section */}
          <div className="aero:flex aero:items-start aero:justify-between aero:mb-1.5">
            <div className="aero:flex-1">
              <div className="aero:flex aero:items-center aero:gap-2">
                <span className="aero:font-inter aero:font-bold aero:text-base aero:leading-6 aero:text-foreground">
                  {machine.name}
                </span>
                <Badge
                  variant="outline"
                  className="aero:font-inter aero:font-normal aero:text-xs aero:leading-4 aero:px-2 aero:py-0.5 aero:rounded-md"
                >
                  {machine.machineIdFormatted}
                </Badge>
              </div>
              <span className="aero:font-inter aero:font-normal aero:text-xs aero:leading-4 aero:text-muted-foreground aero:mt-1">
                {machine?.shop?.shopName}
              </span>
            </div>

            {/* Status Badge & Menu */}
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:flex aero:items-center aero:gap-1">
                <div
                  className={cn(
                    "aero:w-2.5 aero:h-2.5 aero:rounded-full",
                    config?.color
                  )}
                />
                <Badge
                  variant="secondary"
                  className={cn(
                    config?.textColor,
                    config?.bgColor,
                    "aero:border-0"
                  )}
                >
                  {config?.label}
                </Badge>
              </div>

              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    design="text"
                    size="sm"
                    className="aero:h-8 aero:w-8 aero:p-0"
                  >
                    <MoreVertical className="aero:h-4 aero:w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);

                      // avoid race condition with closing dropdown and opening edit sheet (radix issue)
                      setTimeout(() => {
                        onEdit(machine.id.toString());
                      }, 500);
                    }}
                    className="aero:flex aero:items-center aero:gap-2 aero:cursor-pointer"
                  >
                    <Pencil className="aero:h-4 aero:w-4" />
                    Edit Machine
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate({
                        to: "/plant-3/machines/schedule",
                        search: { machineId: String(machine.id) }
                      });
                    }}
                    className="aero:flex aero:items-center aero:gap-2 aero:cursor-pointer"
                  >
                    <Calendar className="aero:h-4 aero:w-4" />
                    View Schedule
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);
                      handleStatusChange(statusConfig.Available.id);
                    }}
                  >
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <div className="aero:w-2 aero:h-2 aero:rounded-full aero:bg-green-500" />
                      Set Available
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);
                      handleStatusChange(statusConfig.Maintenance.id);
                    }}
                  >
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <div className="aero:w-2 aero:h-2 aero:rounded-full aero:bg-yellow-500" />
                      Set Maintenance
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);
                      handleStatusChange(statusConfig.Unavailable.id);
                    }}
                  >
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <div className="aero:w-2 aero:h-2 aero:rounded-full aero:bg-red-500" />
                      Set Unavailable
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setDropdownOpen(false);
                      setShowDeleteDialog(true);
                    }}
                    className="aero:!text-red-600 aero:focus:!text-red-600"
                  >
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <Trash2 className="aero:h-4 aero:w-4 aero:text-destructive" />
                      Delete Machine
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {machine.technicians && machine.technicians.length > 0 && (
            <hr className="aero:my-1 aero:border-gray-200" />
          )}

          {/* Users Section */}
          {machine.technicians && machine.technicians.length > 0 && (
            <div className="aero:space-y-2">
              <div className="aero:flex aero:items-center aero:justify-between aero:mb-1.5">
                <span className="aero:font-inter aero:font-normal aero:text-xs aero:leading-4 aero:text-muted-foreground">
                  User(s) :
                </span>

                {machine.technicians.length > visibleUsersCount && (
                  <Collapsible
                    open={showAllUsers}
                    onOpenChange={setShowAllUsers}
                  >
                    <CollapsibleTrigger className="aero:flex aero:items-center aero:gap-1 aero:font-inter aero:font-normal aero:text-xs aero:leading-4 aero:text-blue-600 aero:hover:text-blue-700">
                      Show All ({machine.technicians.length})
                      <ChevronDown
                        className={cn(
                          "aero:h-4 aero:w-4 aero:transition-transform",
                          showAllUsers && "aero:rotate-180"
                        )}
                      />
                    </CollapsibleTrigger>
                  </Collapsible>
                )}
              </div>

              <div
                className={cn(
                  "aero:flex aero:gap-2",
                  showAllUsers
                    ? "aero:flex-wrap"
                    : "aero:overflow-hidden aero:flex-nowrap"
                )}
              >
                {machine.technicians
                  .slice(0, showAllUsers ? undefined : visibleUsersCount)
                  .map((tech, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className={cn(
                        "aero:flex aero:items-center aero:gap-1.5 aero:px-2.5 aero:py-1 aero:rounded-full aero:bg-white aero:font-inter aero:font-normal aero:text-xs aero:leading-4",
                        !showAllUsers &&
                          "aero:flex-shrink-0 aero:last:flex-shrink aero:last:truncate aero:last:max-w-[100px]"
                      )}
                    >
                      <span className={cn(!showAllUsers && "aero:truncate")}>
                        {tech.name}
                      </span>
                    </Badge>
                  ))}
                {!showAllUsers &&
                  machine.technicians.length > visibleUsersCount && (
                    <Badge
                      variant="secondary"
                      className="aero:px-2.5 aero:py-1 aero:rounded-full aero:bg-blue-100 aero:font-inter aero:font-medium aero:text-xs aero:leading-4 aero:text-blue-700 aero:flex-shrink-0"
                    >
                      +{machine.technicians.length - visibleUsersCount}
                    </Badge>
                  )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showDeleteDialog && (
        <div className="aero:fixed aero:inset-0 aero:z-50 aero:flex aero:items-center aero:justify-center">
          <div
            className="aero:fixed aero:inset-0 aero:bg-black/50 aero:backdrop-blur-sm"
            onClick={() => setShowDeleteDialog(false)}
          />

          <div className="aero:relative aero:bg-white aero:dark:bg-gray-800 aero:rounded-lg aero:shadow-lg aero:p-6 aero:mx-4 aero:max-w-md aero:w-full">
            <div className="aero:mb-4">
              <h3 className="aero:font-inter aero:font-semibold aero:text-lg aero:leading-7 aero:text-gray-900 aero:dark:text-gray-100">
                Delete Machine
              </h3>
              <p className="aero:mt-2 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-gray-600 aero:dark:text-gray-400">
                Are you sure you want to delete <strong>{machine.name}</strong>{" "}
                (ID: {machine.machineId})? This action cannot be undone.
              </p>
            </div>

            <div className="aero:flex aero:gap-3 aero:justify-end">
              <Button
                intent="neutral"
                design="outline"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button
                intent="destructive"
                design="solid"
                onClick={handleDelete}
                className="aero:bg-red-600 aero:hover:bg-red-700"
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
